import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import {
  VpHookFormWrapper,
  VpAutoSave,
  VpMediaTitleInput,
  VpMediaDescriptionInput
} from '@vp-ui';
import { VpIcon } from '@vp-ui/components';
import { VpAddToLibraryButton, VpAddToPluginButton } from '@vp-ui/widgets/VpMediaTable/VpMediaSubTable';
import config from '@src/config';
import { useUpdateMediaDetails, useMediaTableContext } from '@src/hooks';
import { extractThumbnailPath } from '@src/utils';
import { getMediaThumbnailEditingEnabled } from '@src/redux/modules/auth';
import { useVpSnackbar } from '@vp-ui/hooks/useVpSnackbar';
import { useCaptionPolling, useFetchCaptionText } from '@src/hooks/react-query/useCaptionsData';

const VpMediaInfoPublish = ({ media, customizeThumbnailEnabled, history }) => {
  const { setUpdatingThumbnail, setSelectedSubmissionId } = useMediaTableContext();
  const { addSnackbarError } = useVpSnackbar();
  const { mutate: updateMediaData } = useUpdateMediaDetails();
  const mediaId = media?.mediaId;

  const { data: captionsResponse } = useCaptionPolling(mediaId);
  const captions = Array.isArray(captionsResponse) ? captionsResponse : [];
  const originalCaption = captions.find((c) => !c.isTranslation && c.status === 'completed');

  const { data: captionTextData } = useFetchCaptionText(originalCaption?.id);
  const captionText = captionTextData?.text || (typeof captionTextData === 'string' ? captionTextData : '') || '';

  const firstName = media?.reviewerName?.split(' ')[0];
  const defaultTitle = media?.mediaTitle || firstName || 'My Video';
  const defaultDescription = media?.mediaDescription || captionText || 'Recorded with VideoPeel.';

  const defaultValues = {
    title: defaultTitle,
    description: defaultDescription,
    thumbnailUrl: media?.thumbnailUrl
  };

  const onSubmit = (values) => {
    const thumbnailToUse = values?.thumbnailUrl || media?.thumbnailUrl;
    const thumbnailUrl = extractThumbnailPath(thumbnailToUse);
    const data = {
      title: values?.title || '',
      description: values?.description || '',
      thumbnailUrl
    };
    updateMediaData({ data, mediaId: media?.mediaId });
  };

  const handleCustomizeThumbnail = () => {
    if (customizeThumbnailEnabled) {
      return setUpdatingThumbnail(media?.mediaId);
    }
    addSnackbarError({
      message:
        'Your current plan does not allow customizing video thumbnails. Please upgrade on the Billing page.',
      title: 'Error!',
    });
  };

  return (
    <VpHookFormWrapper key={captionText ? 'with-caption' : 'no-caption'} onSubmit={onSubmit}>
      <VpMediaTitleInput defaultValue={defaultTitle} />
      <VpMediaDescriptionInput defaultValue={defaultDescription} />
      <Stack direction="row" alignItems="center" gap={1}>
        <Button
          variant="outlined"
          color="neutral"
          onClick={handleCustomizeThumbnail}
          sx={{ gap: '10px' }}
        >
          <VpIcon icon="Image" sx={{ width: '16px' }} /> Customize Thumbnail
        </Button>
        {/* TODO: replace with publish-specific help center URL when available */}
        <Tooltip title="Learn more about publishing">
          <IconButton
            component="a"
            href={config.helpCenterLink}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <VpIcon icon="HelpCircle" size="18px" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack gap={1} sx={{ mt: 1 }}>
        <Stack direction="row" gap={1} alignItems="center">
          <VpAddToLibraryButton
            mediaId={media?.mediaId}
            onSuccess={(compilation) => {
              setSelectedSubmissionId(null);
              history.push(`/libraries/${compilation.pageLink}`);
            }}
          />
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setSelectedSubmissionId(null);
              history.push('/libraries');
            }}
          >
            Manage Libraries
          </Button>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          <VpAddToPluginButton mediaId={media?.mediaId} />
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setSelectedSubmissionId(null);
              history.push('/plugins');
            }}
          >
            Manage Plugins
          </Button>
        </Stack>
      </Stack>

      <VpAutoSave onSubmit={onSubmit} defaultValues={defaultValues} />
    </VpHookFormWrapper>
  );
};

VpMediaInfoPublish.propTypes = {
  media: PropTypes.shape({
    mediaTitle: PropTypes.string,
    mediaDescription: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    mediaId: PropTypes.string,
    reviewerName: PropTypes.string
  }),
  customizeThumbnailEnabled: PropTypes.bool,
  history: PropTypes.object
};

const mapStateToProps = (state) => ({
  customizeThumbnailEnabled: getMediaThumbnailEditingEnabled(state)
});

const mapDispatchToProps = {};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VpMediaInfoPublish));



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoPublish.jsx